import { useMemo } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Building2,
  CalendarRange,
  TrendingDown,
  Sparkles,
  Activity,
  Crown,
  Layers,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import KPICard from "../components/KPICard";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { getMockData } from "../lib/mockData";
import { formatPct } from "../lib/csv";

export default function Dashboard() {
  const data = useMemo(() => getMockData(), []);
  const {
    kpis,
    history,
    floorStats,
    departmentStats,
    meetingRoomStats,
    sparklines,
    weekdayDistribution,
    todaySnapshot,
    bestFloor,
    topDepartment,
  } = data;

  const trendData = history.map((d) => ({
    date: d.date.slice(5),
    occupancy: Math.round(d.occupancyRate * 100),
  }));

  const floorChart = floorStats.map((f) => ({
    name: `Floor ${f.floor}`,
    occupancy: Math.round(f.occupancyRate * 100),
  }));

  const deptChart = departmentStats.map((d) => ({
    name: d.department,
    occupancy: Math.round(d.occupancyRate * 100),
  }));

  const weekdayChart = weekdayDistribution.map((w) => ({
    name: w.weekday,
    occupancy: Math.round(w.occupancyRate * 100),
  }));

  const sortedDept = [...departmentStats].sort(
    (a, b) => a.occupancyRate - b.occupancyRate,
  );
  const lowestDept = sortedDept[0];

  const todayDate = new Date(todaySnapshot.iso);
  const todayLabel = todayDate.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  return (
    <div data-testid="page-dashboard">
      <PageHeader
        title="Executive Dashboard"
        subtitle="Real-time signals across 3 floors, 8 departments and 300 workplaces. Last 60 days."
        testId="dashboard-header"
      />

      {/* Live pulse band */}
      <div
        className="mb-6 overflow-hidden rounded-lg border border-blue-100 bg-white shadow-sm"
        data-testid="pulse-band"
      >
        <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-4">
          <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 p-6 text-white">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-blue-100">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
              </span>
              Live pulse
            </div>
            <div className="mt-4 font-heading text-4xl font-semibold tracking-tight">
              {formatPct(todaySnapshot.occupancyRate)}
            </div>
            <div className="mt-1 text-xs text-blue-100">
              {todayLabel} · today's occupancy
            </div>
            <div
              aria-hidden
              className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10 blur-2xl"
            />
          </div>
          <PulseStat
            icon={Layers}
            label="Best floor"
            value={`Floor ${bestFloor.floor}`}
            sub={`${formatPct(bestFloor.occupancyRate)} avg occupancy`}
            tone="emerald"
            testId="pulse-best-floor"
          />
          <PulseStat
            icon={Crown}
            label="Top department"
            value={topDepartment.department}
            sub={`${formatPct(topDepartment.occupancyRate)} avg occupancy`}
            tone="violet"
            testId="pulse-top-dept"
          />
          <PulseStat
            icon={Activity}
            label="Variance signal"
            value={`Δ ${formatPct(topDepartment.occupancyRate - lowestDept.occupancyRate)}`}
            sub={`Top vs ${lowestDept.department}`}
            tone="amber"
            testId="pulse-variance"
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Desk occupancy rate"
          value={formatPct(kpis.deskOccupancyRate)}
          delta={kpis.deskOccupancyDelta}
          icon={Building2}
          testId="kpi-desk-occupancy"
          sparkline={sparklines.deskOccupancy}
          sparklineColor="#2563eb"
          accent="blue"
        />
        <KPICard
          label="Meeting room efficiency"
          value={formatPct(kpis.meetingEfficiency)}
          delta={kpis.meetingEfficiencyDelta}
          icon={CalendarRange}
          testId="kpi-room-efficiency"
          sparkline={sparklines.meetingEfficiency}
          sparklineColor="#0d9488"
          accent="emerald"
        />
        <KPICard
          label="No-show rate"
          value={formatPct(kpis.noShowRate)}
          delta={kpis.noShowDelta}
          inverse
          icon={TrendingDown}
          testId="kpi-no-show"
          sparkline={sparklines.noShow}
          sparklineColor="#e11d48"
          accent="rose"
        />
        <KPICard
          label="Optimization potential"
          value={formatPct(kpis.optimizationPotential)}
          delta={kpis.optimizationDelta}
          icon={Sparkles}
          testId="kpi-optimization"
          sparkline={sparklines.optimization}
          sparklineColor="#7c3aed"
          accent="violet"
        />
      </div>

      {/* Trend + Meeting room summary */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-slate-200 shadow-sm lg:col-span-2" data-testid="card-trend">
          <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100">
            <div>
              <CardTitle className="font-heading text-lg font-medium text-slate-900">
                Occupancy trend · 60 days
              </CardTitle>
              <p className="text-xs text-slate-500">
                Daily desk occupancy, weekday patterns visible
              </p>
            </div>
            <Badge variant="secondary" className="bg-blue-50 text-blue-700">
              Live
            </Badge>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-[280px]" data-testid="trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
                  <defs>
                    <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563eb" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    interval={6}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    domain={[0, 100]}
                    unit="%"
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid #e2e8f0",
                      fontSize: 12,
                    }}
                    formatter={(v) => [`${v}%`, "Occupancy"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="occupancy"
                    stroke="#2563eb"
                    strokeWidth={2}
                    fill="url(#trendFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="card-room-summary">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg font-medium text-slate-900">
              Meeting rooms snapshot
            </CardTitle>
            <p className="text-xs text-slate-500">
              Avg participants vs capacity
            </p>
          </CardHeader>
          <CardContent className="divide-y divide-slate-100 p-0">
            {meetingRoomStats.map((room) => {
              const fill = (room.avgUsage / room.capacity) * 100;
              return (
                <div
                  key={room.id}
                  className="flex items-center justify-between px-5 py-3"
                  data-testid={`room-row-${room.id}`}
                >
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      Room {room.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      Floor {room.floor} · capacity {room.capacity}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="hidden h-2 w-24 overflow-hidden rounded-full bg-slate-100 sm:block">
                      <div
                        className={`h-full ${
                          fill < 50 ? "bg-amber-500" : "bg-blue-600"
                        }`}
                        style={{ width: `${Math.min(100, fill)}%` }}
                      />
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-slate-900">
                        {room.avgUsage}/{room.capacity}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        avg usage
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Floor + Department breakdown */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border-slate-200 shadow-sm" data-testid="card-floor-breakdown">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg font-medium text-slate-900">
              Occupancy by floor
            </CardTitle>
            <p className="text-xs text-slate-500">
              Each floor hosts ~100 workplaces
            </p>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={floorChart} margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} domain={[0, 100]} unit="%" />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(v) => [`${v}%`, "Occupancy"]}
                  />
                  <Bar dataKey="occupancy" fill="#2563eb" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="card-dept-breakdown">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg font-medium text-slate-900">
              Occupancy by department
            </CardTitle>
            <p className="text-xs text-slate-500">
              Variance suggests rebalancing opportunity
            </p>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deptChart} margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    angle={-25}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    domain={[0, 100]}
                    unit="%"
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(v) => [`${v}%`, "Occupancy"]}
                  />
                  <Bar dataKey="occupancy" fill="#0f172a" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekday distribution */}
      <Card
        className="mt-6 border-slate-200 shadow-sm"
        data-testid="card-weekday"
      >
        <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100">
          <div>
            <CardTitle className="font-heading text-lg font-medium text-slate-900">
              Weekday distribution
            </CardTitle>
            <p className="text-xs text-slate-500">
              Average occupancy per day-of-week · seasonality-aware
            </p>
          </div>
          <Badge variant="secondary" className="bg-violet-50 text-violet-700">
            Pattern
          </Badge>
        </CardHeader>
        <CardContent className="p-4">
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={weekdayChart}
                margin={{ top: 8, right: 12, bottom: 0, left: -8 }}
              >
                <CartesianGrid
                  stroke="#e2e8f0"
                  strokeDasharray="3 3"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 100]}
                  unit="%"
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    fontSize: 12,
                  }}
                  formatter={(v) => [`${v}%`, "Avg occupancy"]}
                />
                <Bar
                  dataKey="occupancy"
                  fill="#7c3aed"
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PulseStat({ icon: Icon, label, value, sub, tone, testId }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    violet: "bg-violet-50 text-violet-700",
    amber: "bg-amber-50 text-amber-700",
    blue: "bg-blue-50 text-blue-700",
  };
  return (
    <div className="p-6" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div
          className={`flex h-7 w-7 items-center justify-center rounded-md ${tones[tone] || tones.blue}`}
        >
          <Icon className="h-3.5 w-3.5" />
        </div>
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
          {label}
        </span>
      </div>
      <div className="mt-3 font-heading text-2xl font-semibold tracking-tight text-slate-900">
        {value}
      </div>
      <div className="mt-1 text-xs text-slate-500">{sub}</div>
    </div>
  );
}
